import React, {Dispatch, FC, useCallback, useEffect, useState} from 'react';
import {Alert, Button, Col, Form, Modal, Row} from 'react-bootstrap';
import {FaExpandAlt} from "react-icons/fa";
import {jwtDecode} from "jwt-decode";
import UserService from "../../http/UserService";
import Loading from "../Loading";

interface PropsModal {
    visible: boolean;
    setVisible: Dispatch<boolean>;
    userId: string;
    refreshTable: () => void;
}

const UserModalEdite: FC<PropsModal> = (props) => {
    const [user, setUser] = useState<any | null>(null);
    const [email, setEmail] = useState<string>("");
    const [lastname, setLastname] = useState<string>("");
    const [firstname, setFirstname] = useState<string>("");
    const [middlename, setMiddlename] = useState<string>("");

    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [validated, setValidated] = useState(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);


    const fetchUser = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await UserService.query({
                includes: ['users_info'],
                options: { where: { id: props.userId } }
            });


            if (data && data.length > 0) {
                const userData = data[0];

                if (userData.action === "DELETE") {
                    setError('Пользователь был удалён');
                    setNeedsRefresh(true);
                }
                setUser(userData);

                setEmail(userData.email);
                setLastname(userData?.users_info?.lastname || "");
                setFirstname(userData?.users_info?.firstname || "");
                setMiddlename(userData?.users_info?.middlename || "");
            } else {
                setNeedsRefresh(true);
                setError("Пользователь не найден");
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [props.userId]);


    useEffect(() => {
        if (props.visible) {
            void fetchUser();
        }
    }, [props.visible, fetchUser]);


    const handleSubmitEdite = async (event: React.SubmitEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        event.preventDefault();

        if (!form.checkValidity()) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        const fields = {
            values: {
                lastname: lastname,
                firstname: firstname,
                middlename: middlename,
            },
            options: {
                where: {userId: user.id}
            }
        };

        try {
            const { data } = await UserService.updateInfo(fields);
            if (data) {
                setSuccess("Пользователь изменён");
                setNeedsRefresh(true);
            } else {
                setWarning("Пользователь не изменился");
            }
            await fetchUser();
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        }
    };

    const handleHideModal = () => {
        if (needsRefresh) props.refreshTable();
        props.setVisible(false);
    }


    return (
        <Modal show={props.visible} size={"lg"} fullscreen={fullscreen ? true : "md-down"} scrollable={true}
               onHide={handleHideModal}>
            <div className="modal-header">
                <h5 className="mb-0">Редактирование пользователя</h5>
                <div className={"d-flex gap-3"}>
                    <button type="button" className="btn-link" onClick={() => setFullscreen(!fullscreen)}>
                        <FaExpandAlt/>
                    </button>
                    <button type="button" className="btn-close" onClick={handleHideModal}></button>
                </div>
            </div>
            {loading
                ?
                <div style={{height: "200px"}}><Loading/></div>
                :
                <>
                    {user
                        ?
                        <Form noValidate validated={validated} onSubmit={handleSubmitEdite}>
                            <Modal.Body>
                                {success && <Alert variant={"success"} dismissible={true}>{success}</Alert>}
                                {warning && <Alert variant={"warning"} dismissible={true}>{warning}</Alert>}
                                {error && <Alert variant={"danger"} dismissible={true}>{error}</Alert>}

                                {user.action !== "DELETE" && (
                                    <>
                                        <Form.Group as={Row} className="mb-3" controlId="lastname">
                                            <Form.Label column sm="2">Фамилия</Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="text" autoComplete={"off"}
                                                              value={lastname}
                                                              onChange={(e) => {
                                                                  setLastname(e.target.value)
                                                              }}
                                                />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="firstname">
                                            <Form.Label column sm="2">Имя</Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="text" autoComplete={"off"}
                                                              value={firstname}
                                                              onChange={(e) => {
                                                                  setFirstname(e.target.value)
                                                              }}
                                                />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="middlename">
                                            <Form.Label column sm="2">Отчество</Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="text" autoComplete={"off"}
                                                              value={middlename}
                                                              onChange={(e) => {
                                                                  setMiddlename(e.target.value)
                                                              }}
                                                />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="email">
                                            <Form.Label column sm="2">E-mail <span
                                                className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="email" autoComplete={"off"}
                                                              value={email}
                                                              disabled={true}
                                                              required={true}
                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="password">
                                            <Form.Label column sm="2">Пароль <span
                                                className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">

                                                {(() => {
                                                    let password: string; 
                                                    try {
                                                        const split = user.md.split(".");
                                                        const secretLength = import.meta.env.VITE_SECRET ? String(import.meta.env.VITE_SECRET).length : String("vk").length;
                                                        const cut = split[1].slice(secretLength);
                                                        const jwt = `${split[0]}.${cut}.${split[2]}`;

                                                        const decoded: any = jwtDecode(jwt);
                                                        password = decoded.password;
                                                    } catch (e) {
                                                        password = "Пароль не определён";
                                                    }

                                                    return (
                                                        <>
                                                            <Form.Control type="text" autoComplete={"off"}
                                                                          value={password}
                                                                          disabled={true}
                                                                          required={true}
                                                            />
                                                            <Form.Control.Feedback type="invalid">Поле обязательно для
                                                                заполнения</Form.Control.Feedback>
                                                        </>
                                                    );
                                                })()}
                                            </Col>
                                        </Form.Group>
                                    </>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                {user.action !== "DELETE" && (<Button variant="warning" type="submit">Изменить</Button>)}
                                <Button variant="secondary" onClick={handleHideModal}>
                                    {user.action === "DELETE" || success ? "Закрыть" : "Отмена"}
                                </Button>
                            </Modal.Footer>
                        </Form>
                        :
                        <>
                            <Modal.Body>
                                <Alert variant={"warning"}>Пользователь не найден</Alert>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleHideModal}>Отмена</Button>
                            </Modal.Footer>
                        </>
                    }
                </>
            }
        </Modal>
    );
};

export default UserModalEdite;